import React, { useEffect, useState } from "react";
import { useStatusContext } from "../Page/status_context";
import { useSidebar } from "../ui/sidebar";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import LoadingScreenDenda from "../Unique/Animation/loading_screen";
import calendarGameMapping from "./CalendarEditComponents/calendar_game_mapping";

const updateChallenge = (gameId, date, newChallenge) => {
  return fetch(process.env.REACT_APP_CMC_API + "/statistics", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "updateDailyChallenge",
      game: gameId,
      date: date,
      info: newChallenge,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to update challenge");
      }
    })
    .catch((error) => {
      console.error("Error updating challenge:", error);
    });
};

// Utility to format date as YYYY-MM-DD in local time
function formatDateLocal(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Custom advanced calendar component
function CustomCalendar({ selectedDate, onDayClick, challengeDates = [], today, modifiers = {}, modifiersClassNames = {} }) {
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date());

  // Helper to get days in month
  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }
  // Helper to get first day of week (0=Sun)
  function getFirstDayOfWeek(year, month) {
    return new Date(year, month, 1).getDay();
  }

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfWeek = getFirstDayOfWeek(year, month);

  // Build calendar grid
  const weeks = [];
  let week = [];
  let dayNum = 1 - firstDayOfWeek;
  for (let i = 0; i < 6; i++) { // up to 6 weeks
    week = [];
    for (let j = 0; j < 7; j++, dayNum++) {
      if (dayNum < 1 || dayNum > daysInMonth) {
        week.push(null);
      } else {
        week.push(new Date(year, month, dayNum));
      }
    }
    weeks.push(week);
  }

  // Format date as YYYY-MM-DD
  function formatDateLocal(dateObj) {
    if (!dateObj) return "";
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Navigation handlers
  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // Weekday labels
  const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="bg-[#ffffff] dark:bg-[#2c313c] rounded-lg p-4 w-full max-w-md mx-auto select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-700">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div className="font-semibold text-lg">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-700">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>
        </button>
      </div>
      {/* Weekday headers */}
      <div className="grid grid-cols-7 text-center text-xs font-medium text-gray-500 mb-1">
        {weekdayLabels.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>
      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((dateObj, idx) => {
          if (!dateObj) {
            return <div key={idx} className="aspect-square" />;
          }
          const dateStr = formatDateLocal(dateObj);
          const isSelected = selectedDate && dateStr === selectedDate;
          const isToday = today && dateStr === today;
          const hasChallenge = challengeDates.includes(dateStr);
          const isPast = today && dateStr < today;
          // Color logic with dark mode support:
          let bg = "bg-red-100 dark:bg-red-900 dark:text-white";
          if (isSelected) bg = "bg-blue-200 border-2 border-blue-500 dark:bg-blue-900 dark:text-white";
          else if (isToday && hasChallenge) bg = "bg-green-300 dark:bg-green-700 dark:text-white";
          else if (isToday) bg = "bg-red-300 dark:bg-red-700 dark:text-white";
          else if (isPast && hasChallenge) bg = "bg-sky-100 dark:bg-sky-900 dark:text-white";
          else if (isPast && !hasChallenge) bg = "bg-red-100 dark:bg-red-900 dark:text-white";
          else if (!isPast && hasChallenge) bg = "bg-green-100 dark:bg-green-900 dark:text-white";
          else if (!isPast && !hasChallenge) bg = "bg-red-100 dark:bg-red-900 dark:text-white";

          return (
            <button
              key={dateStr}
              className={`aspect-square rounded transition-colors flex items-center justify-center text-base focus:outline-none ${bg} ${isSelected ? 'text-[#000000] dark:text-white' : ''}`}
              onClick={() => onDayClick(dateObj)}
              tabIndex={0}
            >
              {dateObj.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CalendarChallengeViewer = ({ game_id, onDateChange, onChallengeUpdated }) => {
  const { dates, setAuthenticated } = useSidebar();
  const statusContext = useStatusContext();
  const [challenges, setChallenges] = useState([]);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [enteredNumber, setEnteredNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Provide fallback functions if context is not available
  const setStatus = statusContext?.setStatus || (() => {});
  const setLoading = statusContext?.setLoading || (() => {});
  const today = formatDateLocal(new Date());

  // Refactored fetchChallenges function
  const fetchChallenges = async () => {
    setIsLoading(true);
    setLoading(true);
    setStatus({ text: "Fetching data...", color: 2 });
    try {
      const url = process.env.REACT_APP_CMC_API + "/statistics?method=getDailyChallenge&gameid=" + game_id + "&date=" + dates[0] + "_2100-01-01";
      const response = await fetch(url, {
        headers: { Connection: "close" },
        credentials: "include",
      });
      if (response.status === 200) {
        const data = await response.json();
        const challenges = data.date.map((date, index) => ({
          date,
          challenge: data.daily_challenge_info[index],
        }));
        setChallenges(challenges);
        setStatus({ text: "Fetching successful!", color: 0 });
        // Update selectedChallenge and related state if selectedDate is set
        if (selectedDate) {
          const found = challenges.find(c => c.date === selectedDate);
          setSelectedChallenge(found ? { date: found.date, challenge: found.challenge } : null);
          if (found && found.challenge) {
            const [challenge, value] = found.challenge.split("-");
            setSelectedOption(challenge || "");
            setEnteredNumber(value || "");
          } else {
            setSelectedOption("");
            setEnteredNumber("");
          }
        }
      } else if (response.status === 401) {
        setAuthenticated(false);
      } else {
        throw new Error("Failed to fetch challenges");
      }
    } catch (error) {
      setStatus({ text: "Error fetching data", color: 1 });
      setError(error.message);
    }
    setIsLoading(false);
    setLoading(false);
  };

  useEffect(() => {
    if (game_id == null) return;
    fetchChallenges();
    // eslint-disable-next-line
  }, [dates, game_id, setAuthenticated]);

  const challengeDates = challenges.map((c) => c.date);
  const challengeMap = Object.fromEntries(challenges.map((c) => [c.date, c.challenge]));

  const handleDayClick = (dateObj) => {
    const date = formatDateLocal(dateObj);
    if (challengeMap[date]) {
      setSelectedDate(date);
      setSelectedChallenge({ date, challenge: challengeMap[date] });
      const [challenge, value] = challengeMap[date].split("-");
      setSelectedOption(challenge || "");
      setEnteredNumber(value || "");
      setIsEditing(false);
    } else {
      setSelectedDate(date);
      setSelectedChallenge(null);
      setSelectedOption("");
      setEnteredNumber("");
      setIsEditing(false);
    }
    if (onDateChange) onDateChange(date);
  };

  const handleSave = async () => {
    await updateChallenge(game_id, selectedDate, `${selectedOption}-${enteredNumber}`);
    setChallenges((prev) =>
      prev.map((c) =>
        c.date === selectedDate ? { ...c, challenge: `${selectedOption}-${enteredNumber}` } : c
      )
    );
    setIsEditing(false);
    setSelectedChallenge({ date: selectedDate, challenge: `${selectedOption}-${enteredNumber}` });
    if (onChallengeUpdated) onChallengeUpdated();
  };

  const handleCancel = () => {
    if (selectedChallenge) {
      const [challenge, value] = selectedChallenge.challenge.split("-");
      setSelectedOption(challenge || "");
      setEnteredNumber(value || "");
    } else {
      setSelectedOption("");
      setEnteredNumber("");
    }
    setIsEditing(false);
  };

  const getMenuItems = () => {
    if (game_id === 1 || game_id === 6 || game_id === 7) {
      return [
        { value: "clear", label: "clear" },
        { value: "reach", label: "reach" },
      ];
    } else if (game_id === 2 || game_id === 10) {
      return [
        { value: "combo", label: "combo" },
        { value: "speed", label: "speed" },
        { value: "time", label: "time" },
      ];
    } else if (game_id === 9 || game_id === 11 || game_id === 3 || game_id === 12) {
      return [{ value: "placeholder", label: "placeholder" }];
    } else if (game_id === 13 || game_id === 14) {
      return [{ value: "difficulty", label: "difficulty" }];
    } else {
      return [];
    }
  };

  if (error) {
    return <div>Error fetching challenges: {error}</div>;
  }

  return (
    <div className="w-full max-w-2xl sm:max-w-xl md:max-w-lg lg:max-w-2xl mx-auto px-2 sm:px-4 flex flex-col">
      {/* Custom Calendar */}
      <CustomCalendar
        selectedDate={selectedDate}
        onDayClick={handleDayClick}
        challengeDates={challengeDates}
        today={today}
      />
      {/* Challenge Details Section */}
      {selectedDate && (
        <div className="mt-4 sm:mt-6 p-2 sm:p-4 bg-gray-50 shadow-md rounded w-full dark:bg-[#2c313c] max-w-full overflow-x-auto">
          <div className="mb-2 text-base sm:text-lg font-semibold break-words">Challenge for {selectedDate}</div>
          {!isEditing ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              {selectedChallenge ? (
                <>
                  <span className="text-sm sm:text-base break-words">
                    {selectedOption && enteredNumber
                      ? `${selectedOption} - ${enteredNumber}`
                      : selectedChallenge.challenge}
                  </span>
                  <Button
                    className="bg-gray-50 hover:bg-gray-200 transition-colors shadow-none p-2 min-w-0"
                    onClick={() => setIsEditing(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </Button>
                </>
              ) : (
                <span className="text-sm sm:text-base text-gray-500 dark:text-gray-200">No challenge for this date.</span>
              )}
            </div>
          ) : (
            calendarGameMapping[game_id] ? (
              (() => {
                const PreviewComponent = calendarGameMapping[game_id];
                return (
                  <PreviewComponent
                    challenge={selectedChallenge ? selectedChallenge.challenge : null}
                    date={selectedDate}
                    game_id={game_id}
                    onCancel={() => setIsEditing(false)}
                    onSaved={() => {
                      setIsEditing(false);
                      fetchChallenges(); // Refresh after save
                      if (onChallengeUpdated) onChallengeUpdated();
                    }}
                  />
                );
              })()
            ) : (
              <div className="flex flex-col gap-2 w-full max-w-xs">
                <Select value={selectedOption} onValueChange={setSelectedOption}>
                  <SelectTrigger className="bg-[#ffffff] dark:bg-neutral-800">
                    <SelectValue placeholder="Select challenge type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getMenuItems().map((item) => (
                      <SelectItem
                        className="!bg-[#ffffff] hover:!bg-gray-100 text-black shadow-none dark:bg-neutral-800 dark:hover:!bg-neutral-700 dark:text-white"
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input type="number" value={enteredNumber} onChange={(e) => setEnteredNumber(e.target.value)} className="mt-2 bg-[#ffffff] dark:bg-neutral-800" />
                <div className="flex gap-2 mt-2">
                  <Button className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0" onClick={handleSave}>
                    <svg width="20" height="20" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                  </Button>
                  <Button className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0" variant="ghost" onClick={handleCancel}>
                    <svg width="20" height="20" fill="none" stroke="#F8A5A5" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarChallengeViewer; 